import React, {
  useRef,
  useEffect,
  KeyboardEvent,
  useState,
  useContext,
} from "react"
import tw from "twin.macro"
import { ImageModalProps } from "./StackedImageModule.d"
import Icon from "../../../atoms/Icon"
import { LanguageContext } from "../../../../contexts/Language"
import { motion } from "framer-motion"
import { Image } from "../../../atoms/Image"

const ImageModal: React.FC<ImageModalProps> = ({
  open = false,
  expandedImage,
  expandedImageIndex,
  setExpandedImage,
  setExpandedImageIndex,
  slides,
  onClose,
  ...remainingProps
}) => {
  const [modalShow, setModalShow] = useState(false)
  const modalRef = useRef<HTMLDialogElement>(null)
  const { _ } = useContext(LanguageContext)

  useEffect(() => {
    if (open) {
      modalRef?.current?.focus()
    }

    const timer = setTimeout(() => setModalShow(open), 300)
    return () => clearTimeout(timer)
  }, [open])

  const handleKeyDown = (e: any) => {
    const closeBtn = document.getElementById("modalCloseBtn")
    if (e.key === "Tab" && document.activeElement === closeBtn) {
      modalRef.current?.focus()
    }
  }

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  useEffect(() => {
    if (open) {
      modalRef?.current?.focus()
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
  }, [open])

  const variants = {
    enter: {
      opacity: 0,
    },
    center: {
      x: 0,
      opacity: 1,
    },
    exit: {
      opacity: 0,
    },
  }

  const handlePrevImage = () => {
    if (expandedImageIndex && expandedImageIndex - 1 >= 0) {
      setExpandedImage(slides[expandedImageIndex - 1])
      setExpandedImageIndex(expandedImageIndex - 1)
    } else {
      setExpandedImage(slides[slides.length - 1])
      setExpandedImageIndex(slides.length - 1)
    }
  }

  const handleNextImage = () => {
    if (expandedImageIndex !== null && expandedImageIndex + 1 < slides.length) {
      setExpandedImage(slides[expandedImageIndex + 1])
      setExpandedImageIndex(expandedImageIndex + 1)
    } else {
      setExpandedImage(slides[0])
      setExpandedImageIndex(0)
    }
  }

  return (
    open && (
      <div
        css={[
          tw`flex justify-center items-center transition bg-black bg-opacity-0 focus:(outline-none) hover:(outline-none)`,
          tw` w-screen h-screen fixed z-[9999] left-0 top-0`,
          open && tw`bg-opacity-100`,
        ]}
        onKeyDown={e => {
          if (e.key === "Escape") onClose()
        }}
        {...remainingProps}
      >
        <dialog
          ref={modalRef}
          tabIndex={-1}
          aria-label={"dialog box"}
          role="dialog"
          aria-labelledby="title"
          aria-describedby="description"
          css={[
            tw`transition-all opacity-0 block bg-transparent rounded absolute z-50 transform translate-y-8 p-0`,
            tw`fixed top-0 bottom-0 left-0 right-0 bg-black bg-opacity-0  w-full h-screen text-white outline-none`,
            "z-index: 99999",
            modalShow && tw`opacity-100 translate-y-0`,
          ]}
          {...remainingProps}
        >
          <div
            css={[
              tw`h-auto w-full relative`,
              tw`lg:(h-screen) overflow-hidden`,
            ]}
          >
            <div css={[tw`p-12 max-h-screen`]}>
              <button
                onClick={() => {
                  onClose()
                }}
                css={[
                  tw`flex items-center text-xs text-white mb-4 border border-transparent p-1 uppercase`,
                  tw`focus-visible:(border-white border-dashed border outline-none rounded-none p-1)`,
                ]}
              >
                <Icon.Chevron
                  color="white"
                  direction="left"
                  css={[tw`max-h-4 mr-2 w-4`]}
                />
                {_("BACK")}
              </button>
              <div css={[tw`h-[90vh] w-full relative`, tw` overflow-hidden`]}>
                <motion.div
                  key={expandedImageIndex}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    opacity: { duration: 0.5 },
                  }}
                  css={[tw`h-[90vh] w-full`]}
                >
                  <Image
                    imageData={expandedImage?.image}
                    css={[
                      tw`flex items-center object-contain`,
                      tw`lg:(max-w-7xl  max-h-[90%] w-auto mx-auto)`,
                    ]}
                  />
                </motion.div>
                <button
                  onClick={handlePrevImage}
                  css={[
                    tw`absolute h-16 w-6 top-1/3 left-3`,
                    tw`lg:(w-12 top-1/2 left-8 cursor-pointer border border-transparent p-2)`,
                    tw`lg:focus-visible:(border-white border-dashed border outline-none rounded-none p-2)`,
                    slides.length === 1 && tw`hidden`,
                  ]}
                >
                  <Icon.Chevron
                    color="white"
                    direction="left"
                    css={[tw`max-h-12`]}
                  />
                </button>
                <button
                  onClick={handleNextImage}
                  css={[
                    tw`absolute h-16 w-6 top-1/3 right-3`,
                    tw`lg:(w-12 top-1/2 right-8 cursor-pointer border border-transparent p-2)`,
                    tw`lg:focus-visible:(border-white border-dashed border outline-none rounded-none p-2)`,
                    slides.length === 1 && tw`hidden`,
                  ]}
                >
                  <Icon.Chevron
                    color="white"
                    direction="right"
                    css={[tw`max-h-12`]}
                  />
                </button>
              </div>
            </div>
          </div>
        </dialog>
      </div>
    )
  )
}

export default ImageModal
